import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
      ?.href;
  }

  get hasSubscription() {
    return this.dataset.hasSubscription === 'true';
  }

  get productId() {
    return this.dataset.productId;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);

    // Initialize subscription handling if product has subscription
    if (this.hasSubscription) {
      this.#initializeSubscriptionHandling();
    }

    // Initialize variant selector if product has multiple variants
    if (this.dataset.variantCount && parseInt(this.dataset.variantCount) > 1) {
      this.#initializeVariantSelector();
    }
  }

  /**
   * Initialize variant selector for multi-variant products
   */
  #initializeVariantSelector() {
    const variantSelect = this.querySelector('.quick-add__variant-select');
    if (variantSelect) {
      // Auto-select subscription plan for the initially selected variant
      this.#autoSelectSubscriptionPlan(variantSelect);

      // Update the hidden input when variant changes
      variantSelect.addEventListener('change', (event) => {
        const variantId = event.target.value;
        const variantInput = this.querySelector('input[name="id"][ref="variantId"]');
        if (variantInput) {
          variantInput.value = variantId;
          variantInput.disabled = false;

          // Check if selected variant is available
          const selectedOption = event.target.options[event.target.selectedIndex];
          const addButton = this.querySelector('.quick-add__button--add');
          if (addButton) {
            if (selectedOption.disabled) {
              addButton.disabled = true;
              addButton.textContent = 'Sold Out';
            } else {
              addButton.disabled = false;
            }
          }
        }

        // Dispatch variant change event for visibility manager
        const productId = this.dataset.productId;
        const variantChangeEvent = new CustomEvent('quickadd:variant:changed', {
          detail: {
            variantId: variantId,
            productId: productId
          },
          bubbles: true
        });
        document.dispatchEvent(variantChangeEvent);

        // Auto-select subscription plan based on variant
        this.#autoSelectSubscriptionPlan(event.target);
      });
    }
  }

  /**
   * Auto-select subscription plan based on variant title/price
   */
  #autoSelectSubscriptionPlan(variantSelect) {
    const selectedOption = variantSelect.options[variantSelect.selectedIndex];
    const variantText = selectedOption.textContent.toLowerCase();
    const variantTitle = selectedOption.dataset.variantTitle?.toLowerCase() || variantText;
    const variantPrice = selectedOption.dataset.variantPrice;
    const subscriptionSelect = this.querySelector('.quick-add__subscription-select');

    if (!subscriptionSelect) return;

    // Find the matching subscription plan based on variant text/price
    let matchingPlanFound = false;

    // Check for subscription patterns more precisely
    // Look for "recurring" keyword to identify actual subscriptions
    const isRecurringSubscription = variantText.includes('recurring');

    // Check for monthly subscription patterns
    const isMonthlyVariant = isRecurringSubscription && (
                            variantText.includes('monthly') ||
                            variantText.includes('/mo') ||
                            variantText.includes('month recurring'));

    // Check for annual subscription patterns
    const isAnnualVariant = isRecurringSubscription && (
                           variantText.includes('annual') ||
                           variantText.includes('year recurring') ||
                           variantText.includes('yearly'));

    for (let i = 0; i < subscriptionSelect.options.length; i++) {
      const option = subscriptionSelect.options[i];
      const subscriptionType = option.dataset.subscriptionType;

      if (isMonthlyVariant && subscriptionType === 'monthly') {
        subscriptionSelect.selectedIndex = i;
        matchingPlanFound = true;
        console.log('Auto-selected monthly subscription for variant:', variantText);
        break;
      } else if (isAnnualVariant && subscriptionType === 'annual') {
        subscriptionSelect.selectedIndex = i;
        matchingPlanFound = true;
        console.log('Auto-selected annual subscription for variant:', variantText);
        break;
      }
    }

    // If no match found, default to one-time purchase
    if (!matchingPlanFound) {
      subscriptionSelect.value = '';
      console.log('No matching subscription found, defaulting to one-time purchase');
    }

    // Trigger change event on subscription select
    const changeEvent = new Event('change', { bubbles: true });
    subscriptionSelect.dispatchEvent(changeEvent);
  }

  /**
   * Initialize subscription handling for products with subscription options
   */
  #initializeSubscriptionHandling() {
    const subscriptionSelector = this.querySelector('.quick-add__subscription-select');

    if (subscriptionSelector) {
      // Make sure the selling_plan input is properly set when changed
      subscriptionSelector.addEventListener('change', (event) => {
        const sellingPlanValue = event.target.value;
        console.log('Subscription plan selected:', sellingPlanValue);

        // Update any hidden selling_plan inputs if they exist
        const form = this.querySelector('form');
        if (form) {
          let sellingPlanInput = form.querySelector('input[name="selling_plan"]');
          if (!sellingPlanInput && sellingPlanValue) {
            // Create hidden input if it doesn't exist and we have a value
            sellingPlanInput = document.createElement('input');
            sellingPlanInput.type = 'hidden';
            sellingPlanInput.name = 'selling_plan';
            form.appendChild(sellingPlanInput);
          }
          if (sellingPlanInput) {
            sellingPlanInput.value = sellingPlanValue;
          }
        }
      });
    }

    // Add listener for Appstle subscription widget if it exists
    this.addEventListener('appstle:subscription:selected', (event) => {
      if (subscriptionSelector && event.detail && event.detail.sellingPlanId) {
        subscriptionSelector.value = event.detail.sellingPlanId;
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage(this.productPageUrl);
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );

      await this.updateQuickAddModal(freshHtmlCopy);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Store the HTML for later use
      this.#cachedProductHtml = html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
